
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Package, Image as ImageIcon, Upload, Loader2, Hash, ToggleLeft, ToggleRight, Save } from "lucide-react";
import { Link } from "react-router-dom";
import { adminApi } from "@/lib/api";
import { useAuthStore } from "@/lib/store";
import toast from "react-hot-toast";
import { getSmartProductPlaceholder } from "@/lib/placeholders";
import { uploadFileToS3, generateCollectionImageKey, getS3BucketName } from "@/lib/s3";

export default function AdminCollectionDetail() {
  const params = useParams();
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const collectionId = parseInt(params.id as string);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const [collection, setCollection] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    description: "",
    gradient: "",
    display_order: "0",
    is_active: true,
  });

  useEffect(() => {
    if (token && collectionId) {
      fetchCollection();
    }
  }, [token, collectionId]);

  const fetchCollection = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getCollection(collectionId, token!);
      setCollection(data);
      setFormData({
        title: data.title || "",
        slug: data.slug || "",
        description: data.description || "",
        gradient: data.gradient || "",
        display_order: String(data.display_order || 0),
        is_active: data.is_active !== undefined ? data.is_active : true,
      });
      if (data.image_url) {
        setImagePreview(data.image_url);
      }
    } catch (error: any) {
      console.error("Error fetching collection:", error);
      toast.error(error.response?.data?.message || "Failed to load collection");
      navigate("/admin/collections");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error("File size exceeds 10MB limit");
      return;
    }

    setUploading(true);

    try {
      // Get S3 bucket name from environment variables
      const bucketName = getS3BucketName();

      // Generate S3 key for collection image: collections/{collectionId}/imagefile.{ext}
      const s3Key = generateCollectionImageKey(collectionId, file.name);

      // Upload file directly to S3
      const imageUrl = await uploadFileToS3(file, s3Key, bucketName);

      // Update collection with image URL
      await adminApi.updateCollection(
        collectionId,
        { image_url: imageUrl },
        token!
      );

      toast.success("Image uploaded successfully!");
      setImagePreview(imageUrl);
      await fetchCollection(); // Refresh collection data to get updated image_url
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.error(error.message || "Failed to upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleSave = async () => {
    if (!token) {
      toast.error("Authentication required");
      return;
    }

    if (!formData.title.trim()) {
      toast.error("Collection title is required");
      return;
    }

    setSaving(true);

    try {
      const collectionData = {
        title: formData.title,
        slug: formData.slug,
        description: formData.description || null,
        gradient: formData.gradient || null,
        display_order: parseInt(formData.display_order) || 0,
        is_active: formData.is_active,
      };

      const response = await adminApi.updateCollection(collectionId, collectionData, token);

      if (response.success) {
        toast.success("Collection updated successfully!");
        await fetchCollection();
      } else {
        toast.error(response.message || "Failed to update collection");
      }
    } catch (error: any) {
      console.error("Error updating collection:", error);
      toast.error(error?.response?.data?.message || "Failed to update collection");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-8 w-8 bg-gray-200 rounded-lg animate-pulse" />
          <div>
            <div className="h-7 w-48 bg-gray-200 rounded-lg animate-pulse mb-2" />
            <div className="h-4 w-64 bg-gray-200 rounded-lg animate-pulse" />
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="h-6 w-32 bg-gray-200 rounded-lg animate-pulse mb-4" />
              <div className="space-y-3">
                <div className="h-10 w-full bg-gray-200 rounded-lg animate-pulse" />
                <div className="h-10 w-full bg-gray-200 rounded-lg animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="p-4">
        <div className="text-center py-12">
          <p className="text-gray-500">Collection not found</p>
          <Link
            to="/admin/collections"
            className="text-aarsa-sage-600 hover:text-aarsa-sage-700 mt-4 inline-block"
          >
            Back to Collections
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/collections"
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{collection.title}</h1>
            <p className="text-sm text-gray-500 mt-0.5">Collection Details</p>
          </div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>

      <div className="space-y-4">
        {/* Image Upload */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-3">
              <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                <ImageIcon className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-sm font-bold text-gray-900">Collection Image</h2>
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-center gap-6">
              {imagePreview ? (
                <div className="relative w-40 h-40 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
                  <img
                    src={imagePreview}
                    alt={collection.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = getSmartProductPlaceholder(collection.title, "collection");
                    }}
                  />
                </div>
              ) : collection.gradient ? (
                <div
                  className="w-40 h-40 rounded-lg flex-shrink-0"
                  style={{ background: `linear-gradient(to right, ${collection.gradient})` }}
                />
              ) : (
                <div className="w-40 h-40 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50 flex-shrink-0">
                  <ImageIcon className="w-12 h-12 text-gray-400" />
                </div>
              )}
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  disabled={uploading}
                  className="hidden"
                  id="collection-image-upload"
                />
                <label
                  htmlFor="collection-image-upload"
                  className={`inline-flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all font-medium text-sm cursor-pointer ${
                    uploading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      {imagePreview ? "Change Image" : "Upload Image"}
                    </>
                  )}
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  Upload a single image for this collection. Recommended: Square image, 400x400px or larger. Max size: 10MB
                </p>
                {imagePreview && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Image uploaded successfully
                  </p>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Basic Information */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-3">
              <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                <Package className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-sm font-bold text-gray-900">Basic Information</h2>
            </div>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Collection Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                placeholder="e.g., Bridal Collection, Daily Wear"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Slug
              </label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent font-mono"
                placeholder="collection-slug"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent resize-none"
                placeholder="Enter collection description"
              />
            </div>
          </div>
        </motion.div>

        {/* Gradient */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-3">
              <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                <Package className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-sm font-bold text-gray-900">Gradient</h2>
            </div>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Gradient (Tailwind CSS)
              </label>
              <input
                type="text"
                name="gradient"
                value={formData.gradient}
                onChange={handleInputChange}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                placeholder="e.g., from-aarsa-sage-500 to-aarsa-gold-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional Tailwind CSS gradient classes (e.g., from-aarsa-sage-500 to-aarsa-gold-500)
              </p>
            </div>
          </div>
        </motion.div>

        {/* Settings */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="bg-gradient-to-r from-aarsa-sage-50 to-aarsa-gold-50 border-b border-gray-200 px-4 py-3">
              <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-gradient-to-br from-aarsa-sage-500 to-aarsa-gold-500">
                <Hash className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-sm font-bold text-gray-900">Settings</h2>
            </div>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                Display Order
              </label>
              <input
                type="number"
                name="display_order"
                value={formData.display_order}
                onChange={handleInputChange}
                min="0"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-aarsa-sage-500 focus:border-transparent"
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">
                Lower numbers appear first in listings
              </p>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-0.5">
                  Active Status
                </label>
                <p className="text-xs text-gray-500">
                  {formData.is_active
                    ? "Collection is visible to customers"
                    : "Collection is hidden from customers"}
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setFormData({ ...formData, is_active: !formData.is_active })
                }
                className="flex items-center gap-2"
              >
                {formData.is_active ? (
                  <ToggleRight className="w-6 h-6 text-green-500" />
                ) : (
                  <ToggleLeft className="w-6 h-6 text-gray-400" />
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

